import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  alpha,
  AppBar,
  Avatar,
  Badge,
  Box,
  Divider,
  Drawer,
  IconButton,
  InputBase,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../contexts/AuthContext";
import { UiContext } from "../contexts/UiContext";
import { IconTile } from "../components/ui";

import MenuIcon from "@mui/icons-material/Menu";
import SpaceDashboardOutlinedIcon from "@mui/icons-material/SpaceDashboardOutlined";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import SearchIcon from "@mui/icons-material/Search";
import AccessibilityNewIcon from "@mui/icons-material/AccessibilityNew";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import Seo from "../components/Seo";

const SIDEBAR_W = 264;
const SIDEBAR_COLLAPSED_W = 76;
const COLLAPSE_KEY = "aw_sidebar_collapsed";

function initials(user) {
  const src = (user?.name || user?.email || "U").trim();
  const parts = src.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src[0]?.toUpperCase() || "U";
}

export default function DashboardLayout() {
  const { user, logout, isAdmin } = useContext(AuthContext);
  const ui = useContext(UiContext);
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const loc = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [userMenuEl, setUserMenuEl] = useState(null);
  const [query, setQuery] = useState("");
  const hasAdminBackup = !isAdmin && !!localStorage.getItem("aw_admin_token_backup");

  const isRtl = theme.direction === "rtl";
  const mini = collapsed && !isMobile;
  const sidebarW = mini ? SIDEBAR_COLLAPSED_W : SIDEBAR_W;

  // Close the mobile drawer when the route changes
  useEffect(() => {
    setOpen(false);
  }, [loc.pathname]);

  const toggleCollapsed = () => {
    setCollapsed((c) => {
      try {
        localStorage.setItem(COLLAPSE_KEY, c ? "0" : "1");
      } catch {
        /* ignore */
      }
      return !c;
    });
  };

  const groups = useMemo(() => {
    const g = [
      {
        id: "main",
        label: t("nav_group_main", "ראשי"),
        items: [
          { to: "/overview", label: t("overview", "סקירה"), icon: <SpaceDashboardOutlinedIcon /> },
          { to: "/sites", label: t("sites", "אתרים"), icon: <LanguageOutlinedIcon /> },
          { to: "/usage", label: t("usage", "שימוש"), icon: <InsightsOutlinedIcon /> },
        ],
      },
      {
        id: "account",
        label: t("nav_group_account", "חשבון"),
        items: [
          { to: "/plans", label: t("plans", "חבילות"), icon: <CreditCardOutlinedIcon /> },
          { to: "/settings", label: t("settings", "הגדרות"), icon: <SettingsOutlinedIcon /> },
        ],
      },
    ];
    if (isAdmin) {
      g.push({
        id: "admin",
        label: t("nav_group_admin", "ניהול מערכת"),
        items: [
          { to: "/admin/insights", label: t("admin_insights", "תובנות"), icon: <AssessmentOutlinedIcon />, exact: true },
          { to: "/admin", label: t("admin", "אדמין"), icon: <AdminPanelSettingsOutlinedIcon />, exact: true },
        ],
      });
    }
    return g;
  }, [isAdmin, t]);

  const isActive = (it) => (it.exact ? loc.pathname === it.to : loc.pathname === it.to || loc.pathname.startsWith(it.to + "/"));

  const displayName = user?.name || user?.email?.split("@")[0] || "User";
  const avatarSx = {
    bgcolor: (th) => th.aw.accentSoft,
    color: "primary.main",
    fontWeight: 700,
    fontSize: "0.85rem",
  };

  // In RTL the sidebar sits on the right, so "collapse" points towards it.
  const CollapseIcon = (isRtl ? !mini : mini) ? KeyboardDoubleArrowRightIcon : KeyboardDoubleArrowLeftIcon;

  const navItem = (it) => {
    const selected = isActive(it);
    const btn = (
      <ListItemButton
        key={it.to}
        component={Link}
        to={it.to}
        selected={selected}
        aria-current={selected ? "page" : undefined}
        aria-label={mini ? it.label : undefined}
        sx={{
          minHeight: 42,
          mb: 0.25,
          px: mini ? 0 : 1.25,
          justifyContent: mini ? "center" : "flex-start",
          color: "text.secondary",
          "& .MuiListItemIcon-root": { color: (th) => th.aw.faint },
          "&:hover": { bgcolor: (th) => th.aw.soft, color: "text.primary" },
          "&.Mui-selected": {
            color: "primary.main",
            "& .MuiListItemIcon-root": { color: "primary.main" },
          },
          "&.Mui-focusVisible, &:focus-visible": {
            outline: (th) => `2px solid ${th.palette.primary.main}`,
            outlineOffset: -2,
            bgcolor: (th) => th.aw.soft,
          },
        }}
      >
        <ListItemIcon sx={{ minWidth: mini ? 0 : 36, justifyContent: "center", "& svg": { fontSize: 21 } }}>{it.icon}</ListItemIcon>
        {!mini && <ListItemText primary={it.label} primaryTypographyProps={{ fontWeight: 600, fontSize: "0.9rem", noWrap: true }} />}
      </ListItemButton>
    );
    return mini ? (
      <Tooltip key={it.to} title={it.label} placement={isRtl ? "left" : "right"}>
        {btn}
      </Tooltip>
    ) : (
      btn
    );
  };

  const sidebar = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.paper",
      }}
    >
      {/* Brand */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.25}
        sx={{ height: 64, px: mini ? 0 : 2.25, justifyContent: mini ? "center" : "flex-start", flex: "none" }}
      >
        <Box
          component={Link}
          to="/overview"
          aria-label={t("appName", "Accss") + " – " + t("overview", "סקירה")}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            color: "text.primary",
            textDecoration: "none",
            borderRadius: 1.5,
            "&:focus-visible": { outline: (th) => `2px solid ${th.palette.primary.main}`, outlineOffset: 3 },
          }}
        >
          <IconTile variant="solid" size={36}>
            <AccessibilityNewIcon />
          </IconTile>
          {!mini && (
            <Typography sx={{ fontWeight: 800, fontSize: "1.2rem", letterSpacing: "-0.02em" }}>
              {t("appName", "Accss")}
            </Typography>
          )}
        </Box>
      </Stack>

      {/* Navigation */}
      <Box
        component="nav"
        aria-label={t("nav_main_label", "ניווט ראשי")}
        sx={{ flex: 1, overflowY: "auto", px: mini ? 1.25 : 1.5, pt: 1, pb: 2 }}
      >
        {groups.map((g, gi) => (
          <Box key={g.id} sx={{ mt: gi === 0 ? 0 : 2 }}>
            {mini ? (
              gi > 0 ? <Divider sx={{ mb: 1.5, mx: 1 }} /> : null
            ) : (
              <Typography
                component="h2"
                variant="overline"
                sx={{ display: "block", px: 1.25, pb: 0.5, color: (th) => th.aw.faint, lineHeight: 2 }}
              >
                {g.label}
              </Typography>
            )}
            <List disablePadding>{g.items.map(navItem)}</List>
          </Box>
        ))}
      </Box>

      {/* Footer: collapse + user card */}
      <Box sx={{ flex: "none", p: 1.5, borderTop: 1, borderColor: "divider" }}>
        {!isMobile && (
          <Tooltip title={mini ? t("expand", "הרחב תפריט") : ""} placement={isRtl ? "left" : "right"}>
            <ListItemButton
              onClick={toggleCollapsed}
              aria-expanded={!mini}
              aria-label={mini ? t("expand", "הרחב תפריט") : t("collapse", "מזער תפריט")}
              sx={{
                minHeight: 38,
                mb: 1,
                px: mini ? 0 : 1.25,
                justifyContent: mini ? "center" : "flex-start",
                color: "text.secondary",
                "&:hover": { bgcolor: (th) => th.aw.soft },
              }}
            >
              <ListItemIcon sx={{ minWidth: mini ? 0 : 36, justifyContent: "center", color: (th) => th.aw.faint }}>
                <CollapseIcon fontSize="small" />
              </ListItemIcon>
              {!mini && (
                <ListItemText primary={t("collapse", "מזער תפריט")} primaryTypographyProps={{ fontWeight: 500, fontSize: "0.85rem" }} />
              )}
            </ListItemButton>
          </Tooltip>
        )}

        {mini ? (
          <Stack alignItems="center" spacing={1}>
            <Tooltip title={user?.email || ""} placement={isRtl ? "left" : "right"}>
              <Avatar component={Link} to="/settings" aria-label={t("settings", "הגדרות")} sx={{ ...avatarSx, width: 36, height: 36, textDecoration: "none" }}>
                {initials(user)}
              </Avatar>
            </Tooltip>
            <Tooltip title={t("logout", "התנתקות")} placement={isRtl ? "left" : "right"}>
              <IconButton onClick={logout} aria-label={t("logout", "התנתקות")} size="small" sx={{ color: "text.secondary" }}>
                <LogoutIcon fontSize="small" sx={{ transform: isRtl ? "scaleX(-1)" : "none" }} />
              </IconButton>
            </Tooltip>
          </Stack>
        ) : (
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.25}
            sx={{
              p: 1.25,
              borderRadius: (th) => `${th.aw.radius.md}px`,
              border: 1,
              borderColor: "divider",
              bgcolor: (th) => th.aw.softer,
            }}
          >
            <Avatar sx={{ ...avatarSx, width: 36, height: 36 }}>{initials(user)}</Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography noWrap sx={{ fontWeight: 700, fontSize: "0.85rem", lineHeight: 1.3 }}>
                {displayName}
              </Typography>
              <Typography noWrap sx={{ fontSize: "0.75rem", color: "text.secondary", lineHeight: 1.3 }}>
                {user?.email}
              </Typography>
            </Box>
            <Tooltip title={t("logout", "התנתקות")}>
              <IconButton onClick={logout} aria-label={t("logout", "התנתקות")} size="small" sx={{ color: "text.secondary", "&:hover": { color: "error.main" } }}>
                <LogoutIcon fontSize="small" sx={{ transform: isRtl ? "scaleX(-1)" : "none" }} />
              </IconButton>
            </Tooltip>
          </Stack>
        )}
      </Box>
    </Box>
  );

  const onSearch = (e) => {
    e.preventDefault();
    const q = query.trim();
    navigate(q ? `/sites?q=${encodeURIComponent(q)}` : "/sites");
  };

  const iconBtnSx = { color: "text.secondary", "&:hover": { color: "text.primary", bgcolor: (th) => th.aw.soft } };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "background.default" }}>
      <Seo
        title={`${t("appName", { defaultValue: "Accss" })} | Dashboard`}
        description=""
        path={loc.pathname || "/overview"}
        lang={localStorage.getItem("aw_lang") || "he"}
        noIndex
      />

      {/* Skip link */}
      <Box
        component="a"
        href="#main-content"
        sx={{
          position: "absolute",
          insetInlineStart: 12,
          top: -60,
          zIndex: (th) => th.zIndex.tooltip + 1,
          px: 2,
          py: 1,
          borderRadius: 1.5,
          bgcolor: "primary.main",
          color: "primary.contrastText",
          fontWeight: 700,
          textDecoration: "none",
          "&:focus": { top: 12 },
        }}
      >
        {t("skip_to_content", "דלג לתוכן הראשי")}
      </Box>

      {/* Sidebar */}
      {isMobile ? (
        <Drawer
          anchor="left"
          open={open}
          onClose={() => setOpen(false)}
          ModalProps={{ keepMounted: true }}
          PaperProps={{ sx: { width: SIDEBAR_W, border: "none" } }}
        >
          {sidebar}
        </Drawer>
      ) : (
        <Box
          component="aside"
          sx={{
            width: sidebarW,
            flex: "none",
            position: "sticky",
            top: 0,
            height: "100vh",
            borderRight: 1, // flipped to the inner edge by the RTL stylis plugin
            borderColor: "divider",
            transition: theme.transitions.create("width", { duration: theme.transitions.duration.shorter }),
            overflow: "hidden",
            zIndex: (th) => th.zIndex.appBar + 1,
          }}
        >
          {sidebar}
        </Box>
      )}

      {/* Main column */}
      <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        <AppBar position="sticky" sx={{ top: 0, zIndex: (th) => th.zIndex.appBar }}>
          <Toolbar sx={{ gap: { xs: 1, sm: 1.5 }, minHeight: { xs: 60, sm: 64 }, px: { xs: 1.5, sm: 3, md: 4 } }}>
            {isMobile && (
              <IconButton onClick={() => setOpen(true)} aria-label={t("open_menu", "פתח תפריט")} aria-expanded={open} sx={iconBtnSx}>
                <MenuIcon />
              </IconButton>
            )}
            {isMobile && (
              <Box component={Link} to="/overview" aria-label={t("appName", "Accss")} sx={{ display: "flex", alignItems: "center", gap: 1, color: "text.primary", textDecoration: "none" }}>
                <IconTile variant="solid" size={30}>
                  <AccessibilityNewIcon />
                </IconTile>
                <Typography sx={{ fontWeight: 800, fontSize: "1.05rem", display: { xs: "none", sm: "block" } }}>{t("appName", "Accss")}</Typography>
              </Box>
            )}

            {/* Search */}
            <Box
              component="form"
              role="search"
              onSubmit={onSearch}
              sx={{
                flex: 1,
                maxWidth: 440,
                display: { xs: "none", sm: "flex" },
                alignItems: "center",
                gap: 1,
                height: 40,
                px: 1.5,
                borderRadius: (th) => `${th.aw.radius.md}px`,
                border: 1,
                borderColor: "divider",
                bgcolor: (th) => th.aw.softer,
                transition: "border-color .15s, box-shadow .15s",
                "&:hover": { borderColor: (th) => th.aw.borderStrong },
                "&:focus-within": {
                  borderColor: "primary.main",
                  boxShadow: (th) => `0 0 0 3px ${alpha(th.palette.primary.main, 0.18)}`,
                  bgcolor: "background.paper",
                },
              }}
            >
              <SearchIcon sx={{ color: (th) => th.aw.faint, fontSize: 20 }} aria-hidden />
              <InputBase
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("search_placeholder", "חיפוש אתרים ודומיינים…")}
                inputProps={{ "aria-label": t("search_label", "חיפוש באתרים") }}
                sx={{ flex: 1, fontSize: "0.9rem" }}
              />
            </Box>

            <Box sx={{ flexGrow: 1 }} />

            <Stack direction="row" alignItems="center" spacing={0.5}>
              {hasAdminBackup && (
                <Tooltip title={t("back_to_admin", "חזרה לאדמין")}>
                  <IconButton
                    aria-label={t("back_to_admin", "חזרה לאדמין")}
                    sx={iconBtnSx}
                    onClick={() => {
                      const backup = localStorage.getItem("aw_admin_token_backup");
                      if (!backup) return;
                      localStorage.setItem("aw_token", backup);
                      localStorage.removeItem("aw_admin_token_backup");
                      window.location.href = "/admin";
                    }}
                  >
                    <AdminPanelSettingsOutlinedIcon />
                  </IconButton>
                </Tooltip>
              )}

              <Tooltip title={ui?.isDark ? t("theme_light", "מצב בהיר") : t("theme_dark", "מצב כהה")}>
                <IconButton
                  sx={iconBtnSx}
                  onClick={() => ui?.toggleThemeMode?.()}
                  aria-label={ui?.isDark ? t("theme_light", "מצב בהיר") : t("theme_dark", "מצב כהה")}
                  aria-pressed={!!ui?.isDark}
                >
                  {ui?.isDark ? <LightModeOutlinedIcon /> : <DarkModeOutlinedIcon />}
                </IconButton>
              </Tooltip>

              <Tooltip title={t("notifications", "התראות")}>
                <IconButton aria-label={t("notifications", "התראות")} sx={{ ...iconBtnSx, display: { xs: "none", sm: "inline-flex" } }}>
                  <Badge variant="dot" color="primary" overlap="circular" invisible>
                    <NotificationsNoneOutlinedIcon />
                  </Badge>
                </IconButton>
              </Tooltip>

              <Tooltip title={t("help", "עזרה")}>
                <IconButton
                  component={Link}
                  to="/support"
                  aria-label={t("help", "עזרה")}
                  sx={{ ...iconBtnSx, display: { xs: "none", sm: "inline-flex" } }}
                >
                  <HelpOutlineIcon />
                </IconButton>
              </Tooltip>

              <Divider orientation="vertical" flexItem sx={{ mx: 0.75, my: 1.5, display: { xs: "none", sm: "block" } }} />

              <IconButton
                onClick={(e) => setUserMenuEl(e.currentTarget)}
                aria-label={t("account_menu", "תפריט חשבון")}
                aria-haspopup="menu"
                aria-expanded={Boolean(userMenuEl)}
                aria-controls={userMenuEl ? "aw-user-menu" : undefined}
                sx={{ p: 0.25, borderRadius: "50%" }}
              >
                <Avatar sx={{ ...avatarSx, width: 34, height: 34 }}>{initials(user)}</Avatar>
              </IconButton>
              <Menu
                id="aw-user-menu"
                anchorEl={userMenuEl}
                open={Boolean(userMenuEl)}
                onClose={() => setUserMenuEl(null)}
                onClick={() => setUserMenuEl(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: isRtl ? "left" : "right" }}
                transformOrigin={{ vertical: "top", horizontal: isRtl ? "left" : "right" }}
                PaperProps={{ sx: { mt: 1, minWidth: 240 } }}
              >
                <Box sx={{ px: 2, pt: 1, pb: 1.25 }}>
                  <Typography noWrap sx={{ fontWeight: 700, fontSize: "0.9rem" }}>
                    {displayName}
                  </Typography>
                  <Typography noWrap sx={{ fontSize: "0.78rem", color: "text.secondary" }}>
                    {user?.email}
                  </Typography>
                </Box>
                <Divider sx={{ mb: 0.5 }} />
                <MenuItem component={Link} to="/settings">
                  <ListItemIcon>
                    <SettingsOutlinedIcon fontSize="small" />
                  </ListItemIcon>
                  {t("settings", "הגדרות")}
                </MenuItem>
                <MenuItem component={Link} to="/plans">
                  <ListItemIcon>
                    <CreditCardOutlinedIcon fontSize="small" />
                  </ListItemIcon>
                  {t("plans", "חבילות")}
                </MenuItem>
                <Divider />
                <MenuItem onClick={logout} sx={{ color: "error.main" }}>
                  <ListItemIcon sx={{ color: "inherit" }}>
                    <LogoutIcon fontSize="small" sx={{ transform: isRtl ? "scaleX(-1)" : "none" }} />
                  </ListItemIcon>
                  {t("logout", "התנתקות")}
                </MenuItem>
              </Menu>
            </Stack>
          </Toolbar>
        </AppBar>

        <Box
          component="main"
          id="main-content"
          tabIndex={-1}
          sx={{ flex: 1, px: { xs: 2, sm: 3, md: 4 }, py: { xs: 3, md: 4 }, outline: "none" }}
        >
          <Box sx={{ maxWidth: 1240, mx: "auto", width: "100%" }}>
            <Outlet />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
